import * as render from './apps-render.js';
import * as api from './apps-api.js';
import state from './apps-state.js';
import * as nav from './apps-navigation.js';
import * as folder from './apps-folders.js';
import * as events from './apps-events.js';
import * as drag from './apps-drag.js';
import * as slider from './apps-slider.js';
import * as resizeHandler from './apps-resize.js';

resizeHandler.initResizeHandler();

// Event listeners
export function attachEventListeners() {
    const viewport = document.getElementById('appspaceViewport');
    const screensContainer = document.getElementById('appspaceScreens');
    
    document.addEventListener('mousemove', (e) => {
        if (state.isDraggingIcon && state.dragState) {
            e.preventDefault();
            // Mark that mouse has taken over the drag (handoff from keyboard)
            if (state.dragState.isKeyboardDrag) {
                state.dragState.handedOffToMouse = true;
            }
            drag.updateDragging(e.clientX, e.clientY);
        }
    });
    
    document.addEventListener('touchmove', (e) => {
        if (state.isDraggingIcon && state.dragState) {
            e.preventDefault();
            const touch = e.touches[0];
            // Mark that touch has taken over the drag (handoff from keyboard)
            if (state.dragState.isKeyboardDrag) {
                state.dragState.handedOffToMouse = true;
            }
            drag.updateDragging(touch.clientX, touch.clientY);
        }
    }, { passive: false });
    
    document.addEventListener('mouseup', (e) => {
        if (state.isDraggingIcon && state.dragState) {
            e.preventDefault();
            e.stopPropagation();
            drag.endDragging(e.clientX, e.clientY);
        }
    });
    
    document.addEventListener('touchend', (e) => {
        if (state.isDraggingIcon && state.dragState) {
            e.preventDefault();
            e.stopPropagation();
            const touch = e.changedTouches[0];
            drag.endDragging(touch.clientX, touch.clientY);
        }
    });
    
    // Cancel drag on escape key
    document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && state.isDraggingIcon) {
            drag.cancelDragging();
        }
    });
    
    // Screen drag scrolling
    let startX = 0;
    let currentX = 0;
    let startScreen = 0;
    
    function handleScreenDragStart(e) {
        if (state.isDraggingIcon || state.isInteractingWithIcon) return;
        
        // Ignore right-clicks (they're for context menu)
        if (e.button === 2) return;
        
        // Allow to start, but icon handlers will take priority
        
        const touch = e.touches ? e.touches[0] : e;
        state.isDraggingScreen = true;
        startX = touch.clientX;
        currentX = touch.clientX;
        startScreen = state.currentScreen;
        screensContainer.classList.add('dragging');
        
        // Hide virtual cursor, scroll zones, modal zones, and drop indicators when screen drag starts
        import('./apps-kb-cursor.js').then(cursor => {
            if (cursor && typeof cursor.hideVirtualCursor === 'function') {
                cursor.hideVirtualCursor();
            }
        });
        
        import('./apps-kb-scroll.js').then(scroll => {
            if (scroll && typeof scroll.deactivateScrollZones === 'function') {
                scroll.deactivateScrollZones();
            }
        });
        
        import('./apps-kb-modal.js').then(modal => {
            if (modal && typeof modal.unhighlightModalZone === 'function') {
                modal.unhighlightModalZone('top');
                modal.unhighlightModalZone('bottom');
            }
        });
        
        import('./apps-drag.js').then(drag => {
            if (drag && typeof drag.clearDropIndicators === 'function') {
                drag.clearDropIndicators();
            }
        });
    }
    
    function handleScreenDragMove(e) {
        if (!state.isDraggingScreen || state.isDraggingIcon) return;
        
        const touch = e.touches ? e.touches[0] : e;
        currentX = touch.clientX;
        
        const diff = currentX - startX;
        
        const screenWidth = viewport.clientWidth;
        const offset = (-startScreen * 100) + (diff / screenWidth * 100);
        screensContainer.style.transform = `translateX(${offset}%)`;
    }
    
    function handleScreenDragEnd(_e) {
        if (!state.isDraggingScreen || state.isDraggingIcon) return;
        
        state.isDraggingScreen = false;
        screensContainer.classList.remove('dragging');
        
        const diff = currentX - startX;
        const screenWidth = viewport.clientWidth;
        const threshold = screenWidth * 0.2;

        if (diff < -threshold && startScreen < screensContainer.children.length - 1) {
            nav.goToScreen(startScreen + 1);  // Move forward one page from start
        } else if (diff > threshold && startScreen > 0) {
            nav.goToScreen(startScreen - 1);  // Move back one page from start
        } else {
            nav.goToScreen(startScreen);  // Return to start page
        }
    }
    
    viewport.addEventListener('mousedown', handleScreenDragStart);
    viewport.addEventListener('touchstart', handleScreenDragStart, { passive: true });
    document.addEventListener('mousemove', handleScreenDragMove);
    document.addEventListener('touchmove', handleScreenDragMove, { passive: false });
    document.addEventListener('mouseup', handleScreenDragEnd);
    document.addEventListener('touchend', handleScreenDragEnd);

    events.attachIconEventListeners();
    events.attachViewportContextMenu();
}

navigator.serviceWorker.register('/sw.js');

// Event handlers for modal and overlay
document.addEventListener('DOMContentLoaded', () => {
    // Hide virtual cursor when nav or header elements gain focus
    const setupFocusHandlers = () => {
        const nav = document.getElementById('navigation');
        const header = document.querySelector('.header');
        const pageControls = document.querySelector('.page-controls');
        
        const containers = [nav, header, pageControls].filter(el => el);
        
        containers.forEach(container => {
            // Use event delegation on the container
            container.addEventListener('focusin', (e) => {
                // Check if the focused element is a focusable element
                const isFocusable = e.target.matches('a, button, input, select, textarea, [tabindex]:not([tabindex="-1"])');
                if (isFocusable) {
                    // Import and call hideVirtualCursor
                    import('./apps-kb-cursor.js').then(cursor => {
                        if (cursor && typeof cursor.hideVirtualCursor === 'function') {
                            cursor.hideVirtualCursor();
                        }
                    });
                }
            });
        });
    };
    
    // Setup focus handlers after a brief delay to ensure DOM is ready
    setTimeout(setupFocusHandlers, 100);
    
    // Close folder when clicking overlay (but not when drag ends outside)
    let overlayMouseDownTarget = null;
    
    document.getElementById('folderOverlay').addEventListener('mousedown', (e) => {
        overlayMouseDownTarget = e.target;
    });
    
    document.getElementById('folderOverlay').addEventListener('mouseup', (e) => {
        // Only close if both mousedown and mouseup were on the overlay
        if (e.target.id === 'folderOverlay' && overlayMouseDownTarget?.id === 'folderOverlay') {
            folder.closeFolderView();
        }
        overlayMouseDownTarget = null;
    });

    // Also handle touch events
    let overlayTouchStartTarget = null;

    document.getElementById('folderOverlay').addEventListener('touchstart', (e) => {
        overlayTouchStartTarget = e.target;
    }, { passive: true }); // Keep this passive

    document.getElementById('folderOverlay').addEventListener('touchend', (e) => {
        if (e.target.id === 'folderOverlay' && overlayTouchStartTarget?.id === 'folderOverlay') {
            e.preventDefault(); // Prevent the click from falling through
            e.stopPropagation(); // Stop it from propagating
            folder.closeFolderView();
        }
        overlayTouchStartTarget = null;
    });

    // Close edit modal when clicking outside
    document.getElementById('editModal').addEventListener('click', (e) => {
        if (e.target.id === 'editModal') {
            window.closeEditModal();
        }
    });

    // Global cleanup for orphaned ghosts
    document.addEventListener('mouseup', () => {
        // Clean up orphaned ghost
        if (state.dragGhost && !state.isDraggingIcon) {
            state.dragGhost.remove();
            state.dragGhost = null;
            document.querySelectorAll('.drag-placeholder').forEach(el => {
                el.classList.remove('drag-placeholder');
            });
        }
        
        // Clean up folder drag state
        if (state.folderDragState.isDragging) {
            folder.handleFolderDragEnd();
        }
        
        state.iconPressStart = null;
    });
    
    document.addEventListener('touchend', () => {
        // Clean up orphaned ghost
        if (state.dragGhost && !state.isDraggingIcon) {
            state.dragGhost.remove();
            state.dragGhost = null;
            document.querySelectorAll('.drag-placeholder').forEach(el => {
                el.classList.remove('drag-placeholder');
            });
        }
        
        // Clean up folder drag state
        if (state.folderDragState.isDragging) {
            folder.handleFolderDragEnd();
        }
        
        state.iconPressStart = null;
    });

    document.addEventListener('mousemove', (e) => {
        if (state.isDraggingIcon && state.dragState) {
            e.preventDefault();
            drag.updateDragging(e.clientX, e.clientY);
        }
        
        // Track when ghost exists but drag hasn't started
        if (state.dragGhost && !state.isDraggingIcon && state.iconPressStart) {
            const dx = e.clientX - state.iconPressStart.x;
            const dy = e.clientY - state.iconPressStart.y;
            const distance = Math.sqrt(dx * dx + dy * dy);
            
            if (distance > 5) {
                const iconId = parseInt(state.dragGhost.dataset.iconId);
                const icon = window.icons.find(ic => ic.id === iconId);
                
                if (icon) {
                    state.setDraggingState(true);
                    
                    state.dragState = {
                        icon: icon,
                        originalRegion: icon.region,
                        originalPosition: icon.position,
                        originalFolder: icon.kfolder,
                        currentX: e.clientX,
                        currentY: e.clientY,
                        offsetX: 0,
                        offsetY: 0
                    };
                    
                    state.dragGhost.style.transition = 'none';
                    
                    if (state.openFolder) {
                        const leftZone = document.getElementById('folderScrollZoneLeft');
                        const rightZone = document.getElementById('folderScrollZoneRight');
                        if (leftZone) leftZone.classList.add('active');
                        if (rightZone) rightZone.classList.add('active');
                    } else {
                        document.getElementById('scrollZoneLeft').classList.add('active');
                        document.getElementById('scrollZoneRight').classList.add('active');
                    }
                }
            }
        }
    });

    // Initialize
    api.loadUserInfo();

    // Initialize slider after user info loads
    setTimeout(() => {
        slider.attachIconSizeSlider();
    }, 100);

});
